import { Controller } from '@hotwired/stimulus'
import {
  preferredCodeLanguage,
  setPreferredCodeLanguage
} from '../code_editor_language.js'
import { smoothScrollTo } from '../utilities.js'

export default class extends Controller {
  static targets = ['tablist', 'tab', 'tabpanel']
  static outlets = ['tabs']
  static values = {
    autoAlign: { type: Boolean, default: false },
    id: String,
    useCodeLanguagePreference: { type: Boolean, default: false }
  }

  connect() {
    this.boundJustifyTabs = this.#justifyTabs.bind(this)

    if (this.autoAlignValue) {
      window.addEventListener('resize', this.boundJustifyTabs)
      this.boundJustifyTabs()
    }

    const { hash } = window.location
    let targetId = null

    if (hash) {
      const [namespace, id] = hash.replace('#', '').split('--')
      if (namespace === this.idValue) targetId = id
    }

    const preferredLanguage = this.useCodeLanguagePreferenceValue
      ? preferredCodeLanguage()
      : null

    const hasMatchingTab = this.#findTabById(preferredLanguage || targetId)

    this.activateTab(
      false,
      hasMatchingTab
        ? preferredLanguage || targetId
        : this.tabTargets[0].dataset.tabId,
      // Don't update the URL hash on page load since it causes problems when
      // there are multiple tabs controllers. We also don't want to override
      // any non-tabs hashes. Don't broadcast on load since outlets may not
      // be connected yet and each editor initialises independently.
      { updateURL: false, broadcast: false }
    )
  }

  disconnect() {
    window.removeEventListener('resize', this.boundJustifyTabs)
  }

  activateTab(event, id = null, options = {}) {
    const defaultOptions = { updateURL: true, scroll: true, broadcast: true }
    options = { ...defaultOptions, ...options }

    // When synced from another tablist, skip if this tab doesn't exist
    if (!options.broadcast && !this.#findTabById(id)) return

    // If no matching tab is found, default to the first tab
    if (!this.#findTabById(id)) id = this.tabTargets[0].dataset.tabId

    this.tabTargets.forEach(tab => {
      const active = tab.dataset.tabId === id
      tab.dataset.active = active
      tab.ariaSelected = active
      if (active && (event || options.scroll)) this.#scrollTabIntoView(tab)
    })

    this.tabpanelTargets.forEach(tabpanel => {
      const active = tabpanel.dataset.tabId === id
      tabpanel.dataset.active = active
      tabpanel.ariaExpanded = active
    })

    if (options.updateURL && this.idValue) {
      // append namespaced tab ID hash to URL and add it to the browser's history
      const newURL = new URL(window.location)
      newURL.hash = `${this.idValue}--${id}`
      window.history.pushState(null, '', newURL)
    }

    if (options.broadcast) {
      this.tabsOutlets.forEach(outlet => {
        outlet.activateTab(null, id, {
          broadcast: false,
          scroll: false,
          updateURL: false
        })
      })
    }

    if (this.useCodeLanguagePreferenceValue && options.storePreference) {
      setPreferredCodeLanguage(id)
    }
  }

  toggleTab(event) {
    this.activateTab(event, event.currentTarget.dataset.tabId, {
      storePreference: true
    })
  }

  #findTabById(id) {
    return this.tabTargets.find(tab => tab.dataset.tabId === id)
  }

  #justifyTabs() {
    this.tablistTargets.forEach(tablist => {
      if (tablist.dataset.autoAlign === false) return

      if (tablist.scrollWidth > tablist.clientWidth) {
        tablist.classList.add('justify-start')
        tablist.classList.remove('justify-center')
      } else {
        tablist.classList.add('justify-center')
        tablist.classList.remove('justify-start')
      }
    })
  }

  #scrollTabIntoView(tab) {
    const container = tab.closest('[role="tablist"]')
    const containerRect = container.getBoundingClientRect()
    const targetRect = tab.getBoundingClientRect()

    const offset = targetRect.left - containerRect.left
    const scrollOffset =
      offset - (container.clientWidth / 2 - tab.clientWidth / 2)
    let targetScrollLeft = container.scrollLeft + scrollOffset

    // Clamp to scrollable bounds
    const maxScroll = container.scrollWidth - container.clientWidth
    targetScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScroll))

    smoothScrollTo(targetScrollLeft, container, 'x')
  }
}
